import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  configureChatApi,
  configureSocket,
  connectSocket,
  subscribeSocketHandlers,
  disconnectSocket,
  getConversationsByUser,
  getConversationById,
  isErpNotificationEnabled,
} from "@medipha/chat-core";
import {
  setConversationUnread,
  replaceFromConversations,
  clearChatUnread,
} from "store/slices/chatErpUnreadSlice";
import { showOsNotification } from "utils/osNotification";
import { APP_PREFIX_PATH } from "configs/AppConfig";

const CHAT_ERP_PATH = `${APP_PREFIX_PATH}/chat-erp`;

// Đồng bộ unread count của chat thật (@medipha/chat) độc lập với việc có đang mở trang
// /app/chat-erp hay không. Dùng chung socket singleton với ChatModule (xem
// docs/superpowers/specs/2026-09-03-chat-unread-bubble-design.md) - connectSocket/
// subscribeSocketHandlers của @medipha/chat-core được thiết kế để nhiều nơi gọi song song.
const useChatErpUnreadSync = () => {
  const dispatch = useDispatch();
  const { token, profile } = useSelector((state) => state.auth);
  const chatUserId = profile?.id;

  useEffect(() => {
    if (!chatUserId || !token) return undefined;

    const apiUrl = process.env.REACT_APP_MEDITECH_CHAT_API_URL;

    configureChatApi({
      apiBaseUrl: apiUrl,
      getAccessToken: () => token,
    });
    configureSocket(apiUrl);
    connectSocket(token);

    const resync = async () => {
      try {
        const conversations = await getConversationsByUser();
        dispatch(replaceFromConversations(conversations));
      } catch {
        // Im lặng: đây là tác vụ nền cho một badge phụ, không phải luồng chính của chat.
        // Badge giữ giá trị cũ tới lần resync kế tiếp thành công.
      }
    };

    // Tin nhắn mới -> bắn thông báo NGOÀI trang (kiểu Zalo). Chat đi bằng socket riêng,
    // không qua FCM, nên không dựa vào service worker/Firebase được.
    const isConversationMuted = async (conversationId) => {
      try {
        const res = await getConversationById(conversationId);
        const conversation = res?.data ?? res;
        const me = conversation?.members?.find(
          (m) => String(m.userId ?? m.user?.id ?? m.id) === String(chatUserId)
        );
        return !isErpNotificationEnabled(me);
      } catch {
        return false;
      }
    };

    const handleMessageNew = async (payload) => {
      const msg = payload?.message || payload || {};
      const conversationId =
        msg.conversationId || payload?.conversationId || msg.conversation?.id;
      const senderId = msg.senderId || msg.sender?.id || msg.userId;

      // Tin của chính mình thì thôi.
      if (!conversationId || (senderId && String(senderId) === String(chatUserId))) return;

      // Đang mở đúng hội thoại đó và đang nhìn vào tab -> không cần báo.
      const dangXemHoiThoai =
        document.visibilityState === "visible" &&
        window.location.pathname.includes(`/chat-erp/${conversationId}`);
      if (dangXemHoiThoai) return;

      if (await isConversationMuted(conversationId)) return;

      const senderName =
        msg.sender?.fullName || msg.sender?.name || msg.senderName || "Tin nhắn mới";
      const content =
        typeof msg.content === "string"
          ? msg.content
          : msg.text || msg.body || "Bạn có tin nhắn mới";

      showOsNotification({
        title: senderName,
        body: content,
        icon: msg.sender?.avatarUrl,
        tag: `chat-${conversationId}`, // mỗi hội thoại chỉ giữ 1 thông báo, không dồn đống
        data: { refLink: `${CHAT_ERP_PATH}/${conversationId}` },
      });
    };

    const unsubscribe = subscribeSocketHandlers({
      onConversationUnread: (payload) => {
        const { conversationId, unreadCount } = payload || {};
        dispatch(setConversationUnread({ conversationId, unreadCount }));
      },
      onMessageNew: handleMessageNew,
      onConnected: resync,
      onConnectionRestored: resync,
    });

    const handleOnline = () => resync();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") resync();
    };
    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibility);

    resync();

    return () => {
      unsubscribe();
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibility);
      disconnectSocket();
      dispatch(clearChatUnread());
    };
  }, [chatUserId, token, dispatch]);
};

export default useChatErpUnreadSync;
